import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { Radio } from '@toqio/toqio-web-components';
import { PrimaryButton, SecondaryButton } from '../../../shared/components';
import { DESTINATION_OPTIONS, STATUS_FILTER_OPTIONS, type RawFilterOption } from '../status';
import { FIELD_BG, INK } from './styles';

/**
 * "Payment filters" modal (PRD §2/§5). Built flow-local (overlay + card) rather
 * than the DS `Modal` to match the live sandbox exactly and to avoid the DS
 * modal clipping its first field: a 600px white card (radius 24, padding 30),
 * a pill "Search by" input, single-select Destination + Status radio groups,
 * and a footer with Aplicar (primary/black) + Borrar filtros (accent/yellow).
 * Draft state is local and re-seeded on open, so cancelling leaves the applied
 * filters untouched.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
`;

const Card = styled.div`
  position: relative;
  width: 600px;
  max-width: 100%;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 24px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Close = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${INK};
  font-size: 20px;
  line-height: 1;
`;

const Title = styled.h2`
  margin: 0;
  font:
    700 24px/32px Inter,
    sans-serif;
  color: ${INK};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  font:
    400 16px/24px Inter,
    sans-serif;
  color: ${INK};
`;

const SearchInput = styled.input`
  height: 49px;
  padding: 11px 15px;
  border: 1px solid ${FIELD_BG};
  border-radius: 24px;
  background: ${FIELD_BG};
  font:
    400 16px/24px Inter,
    sans-serif;
  color: ${INK};
  outline: none;

  &::placeholder {
    color: #8f8f8f;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.span`
  font:
    400 16px/24px Inter,
    sans-serif;
  color: ${INK};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 6px;
`;

export type FilterValues = {
  externalClientId: string;
  clientId: string;
  status: string;
};

type Props = {
  visible: boolean;
  initial: FilterValues;
  onApply: (values: FilterValues) => void;
  onClear: () => void;
  onClose: () => void;
};

const RadioGroup: React.FC<{
  options: RawFilterOption[];
  selected: string;
  onSelect: (value: string) => void;
}> = ({ options, selected, onSelect }) => (
  <>
    {options.map((option) => (
      <Radio
        key={option.value}
        label={option.label()}
        isSelected={selected === option.value}
        // Toggling a selected radio clears the filter (back to "all").
        onChange={() => onSelect(selected === option.value ? '' : option.value)}
      />
    ))}
  </>
);

export const FilterModal: React.FC<Props> = ({ visible, initial, onApply, onClear, onClose }) => {
  const [externalClientId, setExternalClientId] = useState(initial.externalClientId);
  const [clientId, setClientId] = useState(initial.clientId);
  const [status, setStatus] = useState(initial.status);

  // Re-seed the draft whenever the modal (re)opens with the applied values.
  useEffect(() => {
    if (!visible) return;
    setExternalClientId(initial.externalClientId);
    setClientId(initial.clientId);
    setStatus(initial.status);
  }, [visible, initial.externalClientId, initial.clientId, initial.status]);

  // Close on Escape.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  if (!visible) return null;

  const clear = () => {
    setExternalClientId('');
    setClientId('');
    setStatus('');
    onClear();
  };

  return (
    <Overlay onMouseDown={onClose} role="dialog" aria-modal="true" aria-label={t`Payment filters`}>
      <Card onMouseDown={(e) => e.stopPropagation()}>
        <Close type="button" aria-label={t`Close`} onClick={onClose}>
          ×
        </Close>
        <Title>
          <Trans>Payment filters</Trans>
        </Title>

        <Field>
          <Label>
            <Trans>Search by</Trans>
          </Label>
          <SearchInput
            placeholder={t`Client Id`}
            value={externalClientId}
            onChange={(e) => setExternalClientId(e.target.value)}
          />
        </Field>

        <Section>
          <SectionTitle>
            <Trans>Destination of funds</Trans>
          </SectionTitle>
          <RadioGroup options={DESTINATION_OPTIONS} selected={clientId} onSelect={setClientId} />
        </Section>

        <Section>
          <SectionTitle>
            <Trans>Status</Trans>
          </SectionTitle>
          <RadioGroup options={STATUS_FILTER_OPTIONS} selected={status} onSelect={setStatus} />
        </Section>

        <Footer>
          <PrimaryButton onClick={() => onApply({ externalClientId, clientId, status })}>
            <Trans>Apply</Trans>
          </PrimaryButton>
          <SecondaryButton onClick={clear}>
            <Trans>Clear filters</Trans>
          </SecondaryButton>
        </Footer>
      </Card>
    </Overlay>
  );
};
